/**
 * Console v2.3 — four screens, one job: get to decisions.
 *
 *   Today      the session deck (one question at a time, swipe or tap)
 *   Questions  the question tree — domains → areas → questions, run per level
 *   Docs       the items lens — domain → subcategory → item workspace
 *   Activity   Runs · Session log · Commits · Overview
 *
 * Data layer (v2.2.1 — built for the API rate limit):
 *   • refresh = ONE tree fetch; only files whose SHA changed are re-read via
 *     the Git Data API blob endpoint (api.github.com — CORS-safe; raw.githubusercontent
 *     rejects the Authorization preflight in browsers)
 *   • a path is only marked "loaded" once its text arrived — failed reads are
 *     retried on the next refresh, never silently considered up to date
 *   • refresh MERGES into local state; it never replaces or "closes" cards
 *   • answers/tags/snoozes/modes are STAGED locally (offline-safe), one
 *     "Confirm & sync" commits everything as a single commit (Git Data API)
 *   • every load / stage / confirm / failure is written to a local session log
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { Activity } from "./components/Activity";
import { Agents } from "./components/Agents";
import { Docs } from "./components/Docs";
import { ItemRead } from "./components/ItemRead";
import { NewsDetail } from "./components/NewsDetail";
import { Portfolio } from "./components/Portfolio";
import { QuestionDetail } from "./components/QuestionDetail";
import { Setup } from "./components/Setup";
import { Today } from "./components/Today";
import { parseActivity, type ActivityEntry } from "./lib/activity";
import { loadDemoItems } from "./lib/demo";
import {
  commitFiles,
  dispatchWorkflow,
  isItemPath,
  isNodeMetaPath,
  isQuestionPath,
  listRepoTree,
  readFileAtSha,
  type RemoteFile,
  type RepoConfig,
} from "./lib/github";
import { parseItem, type Item } from "./lib/item";
import { logEvent } from "./lib/log";
import { hasUnseenNews, latestNews, parseNewsFile, type NewsEntry } from "./lib/news";
import { loadPersons, DEFAULT_PERSONS, type Person } from "./lib/persons";
import {
  buildTree,
  effectiveMode,
  nodeMetaYaml,
  parseNodeMode,
  parseNodeNote,
  type NodeMode,
} from "./lib/nodes";
import {
  answeredContent,
  answerMessage,
  parseQuestion,
  personsMessage,
  reopenContent,
  withPersons,
  withSnooze,
  type AnswerPatch,
  type Question,
} from "./lib/questions";
import {
  loadSkipped,
  orderDeck,
  parseSessionFile,
  saveSkipped,
} from "./lib/session";
import {
  clearStaged,
  confirmMessage,
  stageChange,
  stagedChanges,
  type StagedChange,
} from "./lib/staging";
import { clearConfig, loadConfig, saveConfig } from "./lib/store";

type Tab = "today" | "questions" | "docs" | "activity";
type Overlay =
  | { kind: "none" }
  | { kind: "question"; path: string }
  | { kind: "item"; item: Item; fromQuestion?: string }
  | { kind: "news" }
  | { kind: "agents" }
  | { kind: "settings" };

const SESSION_PATH = "tools/cache/session.json";
const ACTIVITY_PATH = "tools/cache/activity.jsonl";
const NEWS_PATH = "tools/cache/news.jsonl";
const FETCH_CONCURRENCY = 6;
/** Shown in the header + logged at boot: instantly tells which build a phone runs. */
export const APP_VERSION = "v2.5";

const RUN_EXTRAS_KEY = "psychotp.run.extras";

/** Extra agents that ride along triggered runs (default: none selected). */
function loadRunExtras(): Set<string> {
  try {
    const raw = localStorage.getItem(RUN_EXTRAS_KEY);
    if (raw) {
      const arr = JSON.parse(raw) as unknown;
      if (Array.isArray(arr)) {
        return new Set(arr.filter((x): x is string => typeof x === "string"));
      }
    }
  } catch {
    /* storage unavailable: no extras */
  }
  return new Set();
}

function saveRunExtras(ids: ReadonlySet<string>): void {
  try {
    localStorage.setItem(RUN_EXTRAS_KEY, JSON.stringify([...ids]));
  } catch {
    /* ignore */
  }
}

const NEWS_SEEN_KEY = "psychotp.news.seen";

/** Timestamp of the newest digest the founder opened ("" = never opened). */
function loadNewsSeen(): string {
  try {
    return localStorage.getItem(NEWS_SEEN_KEY) ?? "";
  } catch {
    return "";
  }
}

function saveNewsSeen(at: string): void {
  try {
    localStorage.setItem(NEWS_SEEN_KEY, at);
  } catch {
    /* ignore */
  }
}

/** Fetch the given paths (Git Data API blobs), N-way parallel, one retry each. */
async function fetchPaths(
  cfg: RepoConfig,
  tree: Map<string, string>,
  paths: string[],
): Promise<{ ok: RemoteFile[]; failed: number }> {
  const ok: RemoteFile[] = [];
  let failed = 0;
  const queue = [...paths];
  const workers = Array.from({ length: Math.min(FETCH_CONCURRENCY, queue.length) }, async () => {
    for (;;) {
      const path = queue.shift();
      if (path === undefined) return;
      const sha = tree.get(path);
      if (!sha) continue;
      try {
        ok.push(await readFileAtSha(cfg, path, sha));
      } catch {
        // one retry — transient rate limits / flaky mobile networks
        await new Promise((resolve) => setTimeout(resolve, 600));
        try {
          ok.push(await readFileAtSha(cfg, path, sha));
        } catch (e) {
          failed += 1;
          logEvent("warn", `could not load ${path} (${e instanceof Error ? e.message : "error"})`);
        }
      }
    }
  });
  await Promise.all(workers);
  return { ok, failed };
}

/** Staged edits must survive refreshes: re-apply them over freshly parsed files. */
function applyStagedOverlay(
  questions: Question[],
  modes: Map<string, NodeMode>,
  staged: StagedChange[],
): { questions: Question[]; modes: Map<string, NodeMode> } {
  let qs = questions;
  const mm = new Map(modes);
  for (const c of staged) {
    if (c.kind === "mode") {
      const node = c.path.slice(0, -"/_node.yml".length);
      if (c.content === null) mm.delete(node);
      else {
        const mode = parseNodeMode(c.content);
        if (mode === "untouched") mm.delete(node);
        else mm.set(node, mode);
      }
      continue;
    }
    if (c.content === null) continue;
    const parsed = parseQuestion(c.path, c.content);
    if (!parsed) continue;
    const idx = qs.findIndex((q) => q.path === c.path);
    qs = idx >= 0 ? qs.map((q) => (q.path === c.path ? parsed : q)) : [...qs, parsed];
  }
  return { questions: qs, modes: mm };
}

export default function App() {
  const [config, setConfig] = useState<RepoConfig | null>(() => loadConfig());
  const [demo, setDemo] = useState(false);
  const [items, setItems] = useState<Item[]>([]);
  const [questions, setQuestions] = useState<Question[]>([]);
  const [modes, setModes] = useState<Map<string, NodeMode>>(new Map());
  const [modeNotes, setModeNotes] = useState<Map<string, string>>(new Map());
  const [sessionOrder, setSessionOrder] = useState<string[]>([]);
  const [activity, setActivity] = useState<ActivityEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadedOnce, setLoadedOnce] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** >0 when the last refresh could not load this many files (retry via Refresh). */
  const [loadWarn, setLoadWarn] = useState(0);
  const [tab, setTab] = useState<Tab>("today");
  const [overlay, setOverlay] = useState<Overlay>({ kind: "none" });
  const [staged, setStaged] = useState<StagedChange[]>([]);
  const [confirming, setConfirming] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [dispatching, setDispatching] = useState(false);
  const [skipped, setSkipped] = useState<Set<string>>(() => loadSkipped());
  const [persons, setPersons] = useState<Person[]>(DEFAULT_PERSONS);
  const [runExtras, setRunExtras] = useState<Set<string>>(() => loadRunExtras());
  const [news, setNews] = useState<NewsEntry[]>([]);
  const [newsSeenAt, setNewsSeenAt] = useState<string>(() => loadNewsSeen());

  /**
   * path → SHA of the text currently in contentRef. A path is only marked with
   * its SHA AFTER its text actually loaded, so failed reads stay "changed" and
   * are retried on the next refresh. (The old code stored the full tree upfront:
   * a failed read counted as "unchanged" forever — an empty app no refresh could
   * fix. The pure-browser CORS failure of raw.githubusercontent.com did exactly
   * that.)
   */
  const shasRef = useRef<Map<string, string>>(new Map());
  const contentRef = useRef<Map<string, string>>(new Map());
  const inflight = useRef(false);
  const toastTimer = useRef<number | null>(null);

  const showToast = useCallback((text: string) => {
    setToast(text);
    if (toastTimer.current !== null) window.clearTimeout(toastTimer.current);
    toastTimer.current = window.setTimeout(() => setToast(null), 2600);
  }, []);

  const syncStaged = useCallback(() => setStaged(stagedChanges()), []);

  const enterDemo = useCallback(() => {
    setDemo(true);
    setItems(loadDemoItems());
    setQuestions([]);
    setModes(new Map());
    setTab("questions");
  }, []);

  /** Derive all UI state from the content cache (after a merge or a stage). */
  const derive = useCallback(() => {
    const modeMap = new Map<string, NodeMode>();
    const noteMap = new Map<string, string>();
    const parsedItems: Item[] = [];
    const parsedQs: Question[] = [];
    for (const [path, content] of contentRef.current) {
      if (isNodeMetaPath(path)) {
        const node = path.slice(0, -"/_node.yml".length);
        const mode = parseNodeMode(content);
        if (mode !== "untouched") modeMap.set(node, mode);
        const note = parseNodeNote(content);
        if (note) noteMap.set(node, note);
      } else if (isQuestionPath(path)) {
        const q = parseQuestion(path, content);
        if (q) parsedQs.push(q);
      } else if (isItemPath(path)) {
        const item = parseItem(path, content);
        if (item.isItem) parsedItems.push(item);
      }
    }
    parsedQs.sort((a, b) => a.path.localeCompare(b.path));
    parsedItems.sort((a, b) => a.filename.localeCompare(b.filename));

    const stagedNow = stagedChanges();
    const overlaid = applyStagedOverlay(parsedQs, modeMap, stagedNow);
    for (const c of stagedNow) {
      if (c.kind !== "mode" || c.content === null) continue;
      const node = c.path.slice(0, -"/_node.yml".length);
      const note = parseNodeNote(c.content);
      if (note) noteMap.set(node, note);
      else noteMap.delete(node);
    }
    setModes(overlaid.modes);
    setModeNotes(noteMap);
    setItems(parsedItems);
    setQuestions(overlaid.questions);

    const sessionRaw = contentRef.current.get(SESSION_PATH);
    if (sessionRaw) {
      const session = parseSessionFile(sessionRaw);
      setSessionOrder(
        (session?.questions ?? []).map((q) => q.path ?? "").filter((p) => p !== ""),
      );
    }
    const activityRaw = contentRef.current.get(ACTIVITY_PATH);
    if (activityRaw) setActivity(parseActivity(activityRaw));
    const newsRaw = contentRef.current.get(NEWS_PATH);
    if (newsRaw !== undefined) setNews(parseNewsFile(newsRaw));
  }, []);

  /**
   * Refresh: one tree fetch, read only changed files, merge — never replace.
   * Single-flight: overlapping taps are ignored instead of stacking 300 calls.
   */
  const refresh = useCallback(
    async (opts?: { silent?: boolean }) => {
      if (!config) return;
      if (inflight.current) return;
      const silent = opts?.silent === true;
      inflight.current = true;
      if (!silent) {
        setLoading(true);
        setError(null);
      }
      logEvent("info", "refresh: fetching tree");
      try {
        const tree = await listRepoTree(config);
        const prev = shasRef.current;
        const pending: string[] = [];
        for (const [path, sha] of tree) {
          if (prev.get(path) === sha) continue;
          if (
            isItemPath(path) ||
            isQuestionPath(path) ||
            isNodeMetaPath(path) ||
            path === SESSION_PATH ||
            path === ACTIVITY_PATH ||
            path === NEWS_PATH
          ) {
            pending.push(path);
          }
        }
        const { ok, failed } =
          pending.length > 0 ? await fetchPaths(config, tree, pending) : { ok: [], failed: 0 };
        for (const f of ok) {
          contentRef.current.set(f.path, f.content);
          shasRef.current.set(f.path, f.sha);
        }
        for (const path of [...shasRef.current.keys()]) {
          if (!tree.has(path)) {
            shasRef.current.delete(path);
            contentRef.current.delete(path);
          }
        }
        derive();
        setLoadedOnce(true);
        setLoadWarn(failed);
        if (failed > 0) {
          logEvent(
            "warn",
            `refresh: ${ok.length}/${pending.length} loaded — ${failed} file(s) failed, tap Retry`,
          );
        } else {
          logEvent("ok", `refresh done: ${ok.length} loaded, ${tree.size} file(s) in tree`);
        }
      } catch (e) {
        const msg = e instanceof Error ? e.message : "unknown error";
        if (!silent) setError(msg);
        logEvent("error", `refresh failed: ${msg}`);
      } finally {
        inflight.current = false;
        if (!silent) setLoading(false);
      }
    },
    [config, derive],
  );

  /** Escape hatch: forget everything loaded and fetch the full tree again. */
  const fullReload = useCallback(() => {
    shasRef.current.clear();
    contentRef.current.clear();
    void refresh();
  }, [refresh]);

  useEffect(() => {
    if (config) void refresh();
  }, [config, refresh]);

  /** Boot line so the Activity session log shows which build this device runs. */
  useEffect(() => {
    logEvent("info", `console ${APP_VERSION} loaded`);
  }, []);

  /** Load any staged edits from a previous visit. */
  useEffect(() => {
    syncStaged();
  }, [syncStaged]);

  /** Persons roster (agentic/dev/persons.yml) — used for the tag sheet. */
  useEffect(() => {
    if (!config) return;
    let alive = true;
    void (async () => {
      try {
        const people = await loadPersons(config);
        if (alive) setPersons(people);
      } catch {
        /* the roster is optional — tags fall back to raw ids */
      }
    })();
    return () => {
      alive = false;
    };
  }, [config]);

  /** Stage the optimistic local edit for one question file. */
  const stageQuestionEdit = useCallback(
    (
      q: Question,
      content: string,
      kind: StagedChange["kind"],
      message: string,
      logText: string,
    ) => {
      stageChange({ path: q.path, content, message, kind });
      setQuestions((prev) =>
        prev.map((p) => (p.path === q.path ? parseQuestion(q.path, content) ?? p : p)),
      );
      syncStaged();
      logEvent("info", logText);
      showToast("staged — Confirm & sync when ready");
    },
    [showToast, syncStaged],
  );

  const handleAnswer = useCallback(
    (q: Question, patch: AnswerPatch) => {
      stageQuestionEdit(
        q,
        answeredContent(q.raw, patch),
        "answer",
        answerMessage(q, patch),
        `staged answer: ${q.id}`,
      );
    },
    [stageQuestionEdit],
  );

  const handleSetPersons = useCallback(
    (q: Question, ids: string[]) => {
      stageQuestionEdit(
        q,
        withPersons(q.raw, ids),
        "tags",
        personsMessage(q, ids),
        `staged tags: ${q.id} → [${ids.join(", ")}]`,
      );
    },
    [stageQuestionEdit],
  );

  const handleSnooze = useCallback(
    (q: Question, until: string) => {
      stageQuestionEdit(
        q,
        withSnooze(q.raw, until),
        "snooze",
        `snooze: ${q.id} → ${until || "cleared"}`,
        `staged snooze: ${q.id} → ${until || "cleared"}`,
      );
    },
    [stageQuestionEdit],
  );

  /** Area mode: staged like everything else. "Standard" deletes the _node.yml. */
  const handleSetMode = useCallback(
    (node: string, mode: NodeMode | "untouched", note = "") => {
      const path = `${node}/_node.yml`;
      if (mode === "untouched") {
        stageChange({
          path,
          content: null,
          message: `mode: ${node} → default`,
          kind: "mode",
        });
        setModes((prev) => {
          const next = new Map(prev);
          next.delete(node);
          return next;
        });
        setModeNotes((prev) => {
          const next = new Map(prev);
          next.delete(node);
          return next;
        });
        logEvent("info", `staged mode: ${node} → Standard (file will be deleted)`);
      } else {
        const content = nodeMetaYaml(mode, new Date().toISOString().slice(0, 10), note);
        stageChange({
          path,
          content,
          message: mode === "custom" ? `mode: ${node} → custom` : `mode: ${node} → ${mode}`,
          kind: "mode",
        });
        setModes((prev) => {
          const next = new Map(prev);
          next.set(node, mode);
          return next;
        });
        setModeNotes((prev) => {
          const next = new Map(prev);
          if (note.trim()) next.set(node, note.trim());
          else next.delete(node);
          return next;
        });
        logEvent("info", `staged mode: ${node} → ${mode}${note ? ` (“${note}”)` : ""}`);
      }
      syncStaged();
      showToast("staged — Confirm & sync when ready");
    },
    [showToast, syncStaged],
  );

  /** ONE commit for everything staged (Git Data API — ~5 requests). */
  const handleConfirm = useCallback(async () => {
    if (!config) return;
    const changes = stagedChanges();
    if (changes.length === 0) return;
    setConfirming(true);
    setError(null);
    const message = confirmMessage(changes);
    try {
      const { commitSha, shas } = await commitFiles(
        config,
        changes.map((c) => ({ path: c.path, content: c.content })),
        message,
      );
      for (const [path, sha] of shas) {
        shasRef.current.set(path, sha);
      }
      for (const c of changes) {
        if (c.content === null) {
          shasRef.current.delete(c.path);
          contentRef.current.delete(c.path);
        } else {
          contentRef.current.set(c.path, c.content);
        }
      }
      clearStaged();
      syncStaged();
      derive();
      logEvent(
        "ok",
        `confirmed: ${changes.length} file(s) — commit ${commitSha.slice(0, 7)} (${message})`,
      );
      showToast(`✓ synced (${commitSha.slice(0, 7)})`);
    } catch (e) {
      const msg = e instanceof Error ? e.message : "unknown error";
      setError(`${msg} — your staged changes are kept; try Confirm & sync again.`);
      logEvent("error", `confirm failed: ${msg} (staged changes kept)`);
    } finally {
      setConfirming(false);
    }
  }, [config, derive, showToast, syncStaged]);

  /** Trigger a run — explicit, confirmed, optionally limited to one area. */
  const runNow = useCallback(
    async (areas: string, label: string, _domain?: string, opts?: { ignoreModes?: boolean }) => {
      if (!config) return;
      const extrasList = [...runExtras];
      const extraLine = extrasList.length
        ? `\nExtras riding along: ${extrasList.join(", ")}`
        : "";
      const extra = opts?.ignoreModes
        ? "\n\nAll area modes are IGNORED for this run — Paused and Never ask areas are scanned too."
        : "";
      const ok = window.confirm(
        `Trigger a run now for ${label}?${extraLine}${extra}\n\nIt may write new questions into this area.`,
      );
      if (!ok) return;
      setDispatching(true);
      setError(null);
      try {
        // NOTE: no `agents` filter here — `areas` (focus) already narrows the
        // run to the domain(s). Passing the domain as an agent name silently
        // ran NOTHING for architecture (its agent is named `product`).
        await dispatchWorkflow(config, "nightly_agents.yml", {
          areas,
          ...(extrasList.length ? { extras: extrasList.join(",") } : {}),
          ...(opts?.ignoreModes ? { ignore_modes: "true" } : {}),
        });
        logEvent(
          "ok",
          `run triggered: ${areas === "" ? "full nightly" : areas}${extrasList.length ? ` (+${extrasList.join(",")})` : ""}${opts?.ignoreModes ? " (modes ignored)" : ""}`,
        );
        showToast("✓ run triggered");
      } catch (e) {
        const msg = e instanceof Error ? e.message : "unknown error";
        setError(msg);
        logEvent("error", `run trigger failed: ${msg}`);
      } finally {
        setDispatching(false);
      }
    },
    [config, showToast],
  );

  /** Green run button at question level — label follows the area's mode. */
  const questionRunInfo = useCallback(
    (q: Question): { label: string; disabledReason?: string } => {
      const mode = modes.get(q.node) ?? "untouched";
      switch (mode) {
        case "challenge":
          return { label: "Challenge it" };
        case "consolidate":
          return { label: "Wrap it up" };
        case "refine":
          return { label: "Refine it" };
        case "autopilot":
          return { label: "Decide it" };
        case "hold":
          return { label: "Rework it", disabledReason: "This area is on Pause — runs are off." };
        case "waived":
          return { label: "Rework it", disabledReason: "This area is set to Never ask." };
        default:
          return { label: "Rework it" };
      }
    },
    [modes],
  );

  /** Question-level run: rework THIS question, flavored by the area's mode. */
  const runQuestion = useCallback(
    async (q: Question) => {
      if (!config) return;
      const mode = modes.get(q.node) ?? "untouched";
      const verb =
        mode === "autopilot"
          ? "decide it by recommendation (it becomes Answered)"
          : mode === "challenge"
            ? "challenge it with counter-evidence"
            : mode === "consolidate"
              ? "fold it into the area's decisions"
              : mode === "refine"
                ? "sharpen it toward your previous answers"
                : "rework it (refresh the context and options)";
      const ok = window.confirm(
        `Run for this question?\n\nAgents will ${verb} — following the area's mode.`,
      );
      if (!ok) return;
      setDispatching(true);
      setError(null);
      try {
        await dispatchWorkflow(config, "nightly_agents.yml", { question: q.id });
        logEvent("ok", `rework run triggered: ${q.id} (mode: ${mode})`);
        showToast("✓ rework run triggered");
      } catch (e) {
        const msg = e instanceof Error ? e.message : "unknown error";
        setError(msg);
        logEvent("error", `rework trigger failed: ${msg}`);
      } finally {
        setDispatching(false);
      }
    },
    [config, modes, showToast],
  );

  const handleSkip = useCallback((q: Question) => {
    setSkipped((prev) => {
      const next = new Set(prev);
      next.add(q.path);
      saveSkipped(next);
      return next;
    });
  }, []);

  /** Bring every "Later" back: the deck is whole again. */
  const handleShowLater = useCallback(() => {
    setSkipped(new Set());
    saveSkipped(new Set());
  }, []);

  /** Open the repo-news digest (and remember that this one was seen). */
  const openNews = useCallback(() => {
    const latest = latestNews(news);
    if (latest?.generatedAt) {
      setNewsSeenAt(latest.generatedAt);
      saveNewsSeen(latest.generatedAt);
    }
    setOverlay({ kind: "news" });
  }, [news]);

  /** Add/remove an extra agent (reviewer / planner) for triggered runs. */
  const toggleRunExtra = useCallback((id: string) => {
    setRunExtras((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      saveRunExtras(next);
      return next;
    });
  }, []);

  /** Re-ask: a decided question goes back to Open (staged), everything else kept. */
  const handleReask = useCallback(
    (q: Question) => {
      const content = reopenContent(q.raw);
      stageChange({ path: q.path, content, message: `reopen: ${q.id}`, kind: "answer" });
      setQuestions((prev) =>
        prev.map((p) => (p.path === q.path ? parseQuestion(q.path, content) ?? p : p)),
      );
      syncStaged();
      logEvent("info", `staged re-ask: ${q.id}`);
      showToast("staged — Confirm & sync when ready");
    },
    [showToast, syncStaged],
  );

  const deck = orderDeck(questions, { sessionOrder, modes, skipped });
  const quiet = (node: string) => {
    const m = effectiveMode(modes, node);
    return m === "hold" || m === "waived" || m === "autopilot";
  };
  const totalOpen = questions.filter((q) => q.status === "open" && !quiet(q.node)).length;
  const quietHidden = questions.filter((q) => q.status === "open" && quiet(q.node));
  const hiddenByModes = quietHidden.length;
  const hiddenDomains = [...new Set(quietHidden.map((q) => q.domain))].sort();
  const laterCount = questions.filter(
    (q) => q.status === "open" && !quiet(q.node) && skipped.has(q.path),
  ).length;
  const tree = buildTree(questions, items, modes);
  const stagedPaths = new Set(staged.map((c) => c.path));
  const repoUrl = config
    ? `https://github.com/${config.owner}/${config.repo}/blob/${config.branch}`
    : undefined;

  if (!config && !demo) {
    return (
      <Setup
        onSave={(cfg) => {
          saveConfig(cfg);
          setConfig(cfg);
        }}
        onDemo={enterDemo}
      />
    );
  }

  /**
   * Overlays render as full-screen LAYERS over the still-mounted tab, so
   * "‹ Back" returns exactly where you were (level + scroll intact).
   */
  const overlayLayer = (() => {
    if (overlay.kind === "settings" && config) {
      return (
        <Setup
          initial={config}
          onSave={(cfg) => {
            saveConfig(cfg);
            setConfig(cfg);
            setOverlay({ kind: "none" });
            void refresh();
          }}
          onCancel={() => setOverlay({ kind: "none" })}
        />
      );
    }

    if (overlay.kind === "agents" && config) {
      return <Agents config={config} onBack={() => setOverlay({ kind: "none" })} />;
    }

    if (overlay.kind === "item") {
      const item = overlay.item;
      const itemNode = item.subcategory
        ? `agentic/${item.domain}/${item.subcategory}`
        : `agentic/${item.domain}`;
      return (
        <ItemRead
          item={item}
          questions={questions}
          repoUrl={repoUrl}
          stagedPaths={stagedPaths}
          runLabel="Run area"
          onRun={() =>
            void runNow(
              itemNode,
              item.subcategory ? `${item.domain} · ${item.subcategory}` : item.domain,
              item.domain,
            )
          }
          onAnswer={(qq, patch) => handleAnswer(qq, patch)}
          onSnooze={(qq, until) => handleSnooze(qq, until)}
          onOpenQuestion={(qq) => setOverlay({ kind: "question", path: qq.path })}
          onBack={() =>
            setOverlay(
              overlay.fromQuestion
                ? { kind: "question", path: overlay.fromQuestion }
                : { kind: "none" },
            )
          }
        />
      );
    }

    if (overlay.kind === "question") {
      const q = questions.find((x) => x.path === overlay.path);
      if (!q) {
        return (
          <div className="mx-auto w-full max-w-2xl px-4 pt-6 text-center">
            <div className="card p-6 text-sm text-ink-400">
              This question is gone — it may have been answered and folded in.
            </div>
            <button
              className="btn-primary mx-auto mt-4 px-4 py-2 text-xs"
              onClick={() => setOverlay({ kind: "none" })}
            >
              Back
            </button>
          </div>
        );
      }
      return (
        <QuestionDetail
          q={q}
          items={items}
          persons={persons}
          busy={false}
          staged={stagedPaths.has(q.path)}
          runLabel={questionRunInfo(q).label}
          runDisabledReason={questionRunInfo(q).disabledReason}
          onRun={() => void runQuestion(q)}
          onReask={() => handleReask(q)}
          onAnswer={(patch) => handleAnswer(q, patch)}
          onSnooze={(until) => handleSnooze(q, until)}
          onSetPersons={(ids) => handleSetPersons(q, ids)}
          onOpenItem={(item) => setOverlay({ kind: "item", item, fromQuestion: q.path })}
          onBack={() => setOverlay({ kind: "none" })}
        />
      );
    }

    if (overlay.kind === "news") {
      return <NewsDetail entries={news} onBack={() => setOverlay({ kind: "none" })} />;
    }

    return null;
  })();

  return (
    <div className="min-h-screen">
      <header className="sticky top-0 z-20 border-b border-ink-850 bg-ink-950/95 backdrop-blur">
        <div className="mx-auto flex w-full max-w-2xl items-center justify-between px-4 py-2.5">
          <span className="text-sm font-semibold tracking-wide text-ink-300">
            PsychoTp · Console
            <span className="ml-1.5 align-middle text-[10px] font-normal text-ink-600">
              {APP_VERSION}
            </span>
          </span>
          <span className="flex items-center gap-1">
            {config && (
              <button
                className="btn-ghost px-3 py-1.5 text-xs"
                onClick={() => setOverlay({ kind: "agents" })}
              >
                Agents
              </button>
            )}
            {config && (
              <button
                className="btn-ghost px-3 py-1.5 text-xs"
                onClick={() => setOverlay({ kind: "settings" })}
                title="Settings"
              >
                ⚙︎
              </button>
            )}
            {demo && (
              <button
                className="btn-ghost px-3 py-1.5 text-xs"
                title="Exit demo"
                onClick={() => {
                  setDemo(false);
                  setItems([]);
                  setQuestions([]);
                }}
              >
                exit demo
              </button>
            )}
            {!demo && config && (
              <button
                className="btn-ghost px-3 py-1.5 text-xs"
                title="Disconnect this device"
                onClick={() => {
                  clearConfig();
                  setConfig(null);
                  setItems([]);
                  setQuestions([]);
                }}
              >
                disconnect
              </button>
            )}
          </span>
        </div>
      </header>

      {error && (
        <div className="mx-auto w-full max-w-2xl px-4 pt-3">
          <div className="rounded-xl border border-danger/40 bg-danger/10 p-3 text-sm text-danger">
            {error}
          </div>
        </div>
      )}
      {loadWarn > 0 && (
        <div className="mx-auto w-full max-w-2xl px-4 pt-3">
          <button
            onClick={() => void refresh()}
            className="w-full rounded-xl border border-warn/40 bg-warn/10 p-3 text-left text-sm text-warn"
          >
            {loadWarn} file{loadWarn === 1 ? "" : "s"} could not be loaded — tap to retry
          </button>
        </div>
      )}

      {!loadedOnce && loading ? (
        <div className="mx-auto w-full max-w-2xl px-4 pt-10 text-center text-sm text-ink-500">
          Loading…
        </div>
      ) : (
        <>
          {tab === "today" && (
            <Today
              deck={deck}
              totalOpen={totalOpen}
              laterCount={laterCount}
              hiddenByModes={hiddenByModes}
              hiddenDomains={hiddenDomains}
              dispatching={dispatching}
              persons={persons}
              stagedPaths={stagedPaths}
              newsLatest={latestNews(news)}
              newsUnseen={hasUnseenNews(news, newsSeenAt)}
              onOpenNews={openNews}
              onOpenQuestion={(q) => setOverlay({ kind: "question", path: q.path })}
              onSkip={handleSkip}
              onShowLater={handleShowLater}
              onTagQuestion={handleSetPersons}
              onDispatch={() => void runNow("", "the full nightly run")}
              onRefresh={() => void refresh()}
            />
          )}
          {tab === "questions" && (
            <Portfolio
              tree={tree}
              modes={modes}
              modeNotes={modeNotes}
              extras={runExtras}
              onToggleExtra={toggleRunExtra}
              stagedPaths={stagedPaths}
              onSetMode={handleSetMode}
              onOpenItem={(item) => setOverlay({ kind: "item", item })}
              onOpenQuestion={(q) => setOverlay({ kind: "question", path: q.path })}
              onAnswer={handleAnswer}
              onSnooze={handleSnooze}
              onRun={(areas, label, domain) => void runNow(areas, label, domain)}
              onScanAll={() => void runNow("", "a complete scan of everything", undefined, { ignoreModes: true })}
            />
          )}
          {tab === "docs" && (
            <Docs
              items={items}
              questions={questions}
              onOpenItem={(item) => setOverlay({ kind: "item", item })}
            />
          )}
          {tab === "activity" && (
            <Activity
              entries={activity}
              loading={loading}
              config={config}
              questions={questions}
              items={items}
              modes={modes}
              stagedCount={staged.length}
              onRefresh={() => void refresh()}
              onFullReload={fullReload}
            />
          )}
        </>
      )}

      {/* One fixed bottom stack: toast · confirm bar · tab bar — can never overlap. */}
      <div className="fixed inset-x-0 bottom-0 z-20">
        {toast && (
          <div className="pointer-events-none flex justify-center px-4 pb-2">
            <span className="rounded-full border border-accent/40 bg-ink-900/95 px-3.5 py-1.5 text-xs text-accent-soft shadow-lg">
              {toast}
            </span>
          </div>
        )}

        {staged.length > 0 && (
          <div className="px-4 pb-2">
            <div className="mx-auto flex w-full max-w-2xl items-center justify-between gap-3 rounded-xl border border-accent/40 bg-ink-900/95 p-3 shadow-lg backdrop-blur">
              <span className="text-xs text-ink-300">
                <span className="font-semibold text-accent-soft">{staged.length}</span>{" "}
                change{staged.length === 1 ? "" : "s"} staged on this device
              </span>
              <button
                className="btn-primary shrink-0 px-4 py-2 text-xs"
                disabled={confirming}
                onClick={() => void handleConfirm()}
              >
                {confirming ? "syncing…" : `Confirm & sync (${staged.length})`}
              </button>
            </div>
          </div>
        )}

        <nav className="border-t border-ink-850 bg-ink-950/95 backdrop-blur">
          <div className="mx-auto flex w-full max-w-2xl pb-[env(safe-area-inset-bottom)]">
            {(
              [
                ["today", deck.length > 0 ? `Deck (${deck.length})` : "Deck"],
                ["questions", "Questions"],
                ["docs", "Docs"],
                ["activity", "Activity"],
              ] as [Tab, string][]
            ).map(([key, label]) => (
              <button
                key={key}
                onClick={() => setTab(key)}
                className={`flex-1 py-3 text-center text-sm font-medium transition-colors ${
                  tab === key ? "text-accent-soft" : "text-ink-500 hover:text-ink-300"
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </nav>
      </div>

      {/* Overlays: full-screen layers over the mounted tab (back keeps state).
          The top padding keeps ‹ Back / run buttons clear of the iOS status bar. */}
      {overlayLayer && (
        <div className="fixed inset-0 z-40 overflow-y-auto bg-ink-950 pt-[env(safe-area-inset-top)]">
          {overlayLayer}
        </div>
      )}
    </div>
  );
}
